import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Layout } from "../components/Layout";
import { ImageCapture } from "../components/ImageCapture";
import { TypeBadge, FitBadge, Disclaimer } from "../components/Disclaimer";
import { LocationEditor } from "../components/LocationEditor";
import { AlertTriangle, CheckCircle2, Ruler, Bookmark, ArrowRight, Eye } from "lucide-react";
import { toast } from "sonner";

export default function Scan() {
  const { user, usage, refreshUsage } = useAuth();
  const nav = useNavigate();
  const [b64, setB64] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const scanLocked = user?.paid && usage?.applies && !usage.features?.scan?.available;

  const run = async () => {
    if (!b64) return;
    setBusy(true);
    try {
      const { data } = await api.post("/scan", { image_base64: b64 });
      setResult(data);
      refreshUsage();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); refreshUsage(); }
    finally { setBusy(false); }
  };

  const makePath = async (idea) => {
    if (!user?.paid) { toast.message("Saving a path is a member feature"); nav("/paywall"); return; }
    try {
      const { data } = await api.post("/sessions", {
        title: idea.title, source: "scan", summary: idea.howEarnHere,
        pathData: { thisWeek: idea.thisWeek, summary: idea.howEarnHere },
      });
      toast.success("Path started");
      nav(`/session/${data.id}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const pin = async () => {
    if (!user?.paid) { toast.message("Saving is a member feature"); nav("/paywall"); return; }
    try {
      await api.post("/pins", { imageUrl: preview, seenText: result.seen, ideas: result.ideas, place: result.place });
      toast.success("Pinned to Saved");
    } catch { toast.error("Could not pin"); }
  };

  return (
    <Layout>
      <div className="fadeup space-y-5">
        <div>
          <h2 className="font-head font-bold text-2xl sm:text-3xl">Scan anything</h2>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>
            Identify a thing or scene and get local earn ideas — biased to {user?.city || "your place"}.
          </p>
        </div>

        {!user?.city && <LocationEditor />}

        <ImageCapture onImage={(bb, dd) => { setB64(bb); setPreview(dd); setResult(null); }} />

        {b64 && !result && (
          <div className="space-y-2">
            <button onClick={run} disabled={busy || scanLocked} className="pm-btn flex items-center gap-2" data-testid="run-scan-btn">
              <Eye className="w-4 h-4" /> {busy ? "Reading the photo…" : scanLocked ? "Not enough coins for a scan" : `Get earn ideas${user?.paid && usage?.features?.scan ? ` · ${usage.features.scan.cost} coins` : ""}`}
            </button>
            {scanLocked && (
              <p className="text-xs" style={{ color: "var(--warning)" }} data-testid="scan-insufficient-note">A scan costs {usage?.features?.scan?.cost} coins. Buy 50 more at the top of the page or wait for your monthly refresh.</p>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="scan-result">
            <div className="pm-card p-4">
              <div className="label-mono">Place used: {result.place}</div>
              <div className="mt-1"><b>Seen:</b> {result.seen}</div>
            </div>

            {result.ideas?.map((idea, i) => (
              <div key={i} className="pm-card p-5" data-testid={`scan-idea-${i}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2"><TypeBadge type={idea.type} /><FitBadge fit={idea.fit} /></div>
                <h3 className="font-head font-bold text-lg">{idea.title}</h3>
                <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{idea.howEarnHere}</p>
                {idea.needs?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {idea.needs.map((n, k) => <span key={k} className="label-mono px-2 py-0.5 border" style={{ borderColor: "var(--border-subtle)" }}>{n}</span>)}
                  </div>
                )}
                <div className="mt-3 p-3" style={{ background: "var(--surface-subtle)" }}>
                  <div className="label-mono mb-1">This week</div><div className="text-sm">{idea.thisWeek}</div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => makePath(idea)} className="pm-btn flex items-center gap-2 text-sm" data-testid={`scan-makepath-${i}`}>
                    Make path <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {result.waste?.whyWasteHere && (
              <div className="pm-card p-4" style={{ borderColor: "var(--warning)" }} data-testid="scan-waste">
                <div className="label-mono flex items-center gap-2" style={{ color: "var(--warning)" }}><AlertTriangle className="w-4 h-4" /> Waste / skip</div>
                <p className="text-sm mt-2"><b>Tempting:</b> {result.waste.tempting}</p>
                <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>{result.waste.whyWasteHere}</p>
                <p className="text-sm mt-2 flex items-start gap-1.5"><CheckCircle2 className="w-4 h-4 mt-0.5" style={{ color: "var(--earn)" }} /> <span><b>Instead:</b> {result.waste.instead}</span></p>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <button onClick={pin} className="pm-btn-ghost flex items-center gap-2 text-sm" data-testid="scan-pin-btn"><Bookmark className="w-4 h-4" /> Save / Pin</button>
              {result.measurable && (
                <button onClick={() => nav("/measure")} className="pm-btn-ghost flex items-center gap-2 text-sm" data-testid="scan-measure-btn"><Ruler className="w-4 h-4" /> Measure this</button>
              )}
            </div>
          </div>
        )}
        <Disclaimer compact />
      </div>
    </Layout>
  );
}
